import {
  AddScheduleEmisionIcon,
  Column,
  ItemSkeleton,
  TablePaginated,
  TablePaginatedOptions,
} from '@acceso/design-system';
import { DragIcon } from '@acceso/design-system';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { RenderActionButtons } from '@/components/RenderActionButtons';
import { TableRow } from '@/components/TableRow';
import { useAudienceChannelSources } from '@/features/broadcasters/api/get-audience-channel-sources';
import { useAudienceChannelSourceOptions } from '@/features/broadcasters/hooks/useAudienceChannelSourceOptions';
import { AudienceProviderEnum } from '@/features/broadcasters/types';
import { useGetProgramsAudienceProgramSources } from '@/features/programs/api/get-audience-program-sources';
import { useGetDatetimeRangeFormatted } from '@/hooks/useDatetimeRangeFormatted';
import { useTableMultiselect } from '@/hooks/useTableMultiselect';
import { routerManager } from '@/routes/app-routes';
import { MediaManagerRouteEnum } from '@/routes/constants/MediaManagerRouteEnum';
import { TableSelection } from '@/types';
import { withAppTzDateFilter } from '@/utils/app-tz-date-range';
import { createTableFilterOptions, getParamsFromTableFilters } from '@/utils/table-filters';

import { useAudiences } from '../../api/get-audience';
import { AUDIENCE_PROVIDER_CONFIG, SCHEDULE_AUDIENCE_PROVIDERS } from '../../constants';
import { useAudienceFilters, useFavoriteAudienceFilters } from '../../stores/audience-filters';
import { AugmentedAudience, TAudience } from '../../types';

type AudiencesTableProps = {
  onAddScheduleEmision?: (selection: TableSelection<TAudience>) => void;
};

export function AudiencesTable({ onAddScheduleEmision }: AudiencesTableProps) {
  const { t } = useTranslation();
  const getDatetimeRangeFormatted = useGetDatetimeRangeFormatted();
  const [channelSourceSearch, setChannelSourceSearch] = useState<string>('');

  const {
    paginationValues,
    setSearchQuery,
    filters,
    setPage,
    setRows,
    setSortBy,
    setFilters,
    clearFilters,
  } = useAudienceFilters();
  /**
   * What the operator selected in the "Proveedor" filter, or every provider this
   * list works with when nothing is selected. `??` so a cleared filter — `null` in
   * the store's shape — falls back, while an empty array does not: that one means
   * "nothing selected" and is dropped by the api layer.
   *
   * The same set scopes the list and the "Emisora" options, so the control can
   * never offer a source whose registries the list is not asking for.
   */
  const requestedProviders = filters?.providerIn ?? SCHEDULE_AUDIENCE_PROVIDERS;

  const audienceChannelSourceOptions = useAudienceChannelSourceOptions({
    name: channelSourceSearch,
    includeIds: filters?.channelSourceIdIn ?? undefined,
    providerIn: requestedProviders,
  });
  const { data: { data: audiences = [], total = 0 } = {}, isLoading: isLoadingAudiences } =
    useAudiences({
      params: getParamsFromTableFilters({
        paginationValues,
        filters: {
          ...filters,
          search: paginationValues.query,
          providerIn: requestedProviders,
        },
      }),
    });

  const audienceChannelSourceIds = new Set(audiences.map(audience => audience.channel_source_id));
  const { data: audienceChannelSources = [], isLoading: isLoadingAudienceChannelSources } =
    useAudienceChannelSources({
      params: {
        idIn: [...audienceChannelSourceIds],
      },
      // With no ids the request goes out unfiltered and returns every source.
      config: { enabled: audienceChannelSourceIds.size > 0 },
    });
  const audienceChannelSourceById = new Map(
    audienceChannelSources.map(audienceChannelSource => [
      audienceChannelSource.id,
      audienceChannelSource,
    ])
  );
  const audienceProgramSourceIds = new Set(
    audiences.map(audience => audience.program_source_id).filter(id => id !== null)
  );
  const {
    data: programsAudienceProgramSources = [],
    isLoading: isLoadingProgramsAudienceProgramSources,
  } = useGetProgramsAudienceProgramSources({
    idIn: [...audienceProgramSourceIds],
    // The API reads an empty `id__in` as "no filter" and returns every source.
    config: { enabled: audienceProgramSourceIds.size > 0 },
  });

  const augmentedAudiences: AugmentedAudience[] = audiences.map(audience => ({
    ...audience,
    program_source: programsAudienceProgramSources.find(
      audienceProgramSource => audienceProgramSource.id === audience.program_source_id
    ),
  }));

  const { checkboxColumn, multiSelectionButtonProps } = useTableMultiselect<TAudience>({
    itemsCount: audiences.length,
    actions: [
      {
        icon: AddScheduleEmisionIcon,
        onClick: selection => {
          onAddScheduleEmision?.(selection);
        },
        tooltip: t('Añadir a parrilla'),
      },
    ],
  });

  const { state: favoriteAudienceFilters, setState: setFavoriteAudienceFilters } =
    useFavoriteAudienceFilters();

  const options: Partial<TablePaginatedOptions> = {
    pagination: {
      changePage: setPage,
      changeRows: setRows,
      currentPage: paginationValues.currentPage,
      itemsPerPage: paginationValues.rowsPerPage,
    },
    onCleanAllFilters: () => {
      clearFilters();
    },
    setSortBy,
    sortBy: paginationValues.sortBy,
    onSearchChange: setSearchQuery,
    searchBarPlaceholder: t('Buscar por nombre'),
    dateFilter: withAppTzDateFilter({
      name: 'date',
      placeholder: t('Fecha'),
      showHours: true,
      onChange: value => {
        setFilters({
          startDatetimeGte: value.start?.toISOString(),
          endDatetimeLte: value.end?.toISOString(),
        });
      },
    }),
    filters: [
      {
        name: 'provider',
        placeholder: t('Proveedor'),
        options: createTableFilterOptions({
          options: SCHEDULE_AUDIENCE_PROVIDERS.map(provider => ({
            label: AUDIENCE_PROVIDER_CONFIG[provider].label,
            value: provider,
          })),
          allOptionLabel: t('Cualquier proveedor'),
        }),
        onChange: value => {
          // The "all" option carries an empty value, so it drops out here and the
          // request falls back to every provider this list works with.
          const providers = (Array.isArray(value) ? value : [value]).filter(
            Boolean
          ) as AudienceProviderEnum[];

          setFilters({
            providerIn: providers.length > 0 ? providers : undefined,

            /**
             * A source of another provider is no longer offered by the "Emisora"
             * control, and would otherwise keep filtering the list to nothing with
             * nothing on screen to say why.
             */
            channelSourceIdIn: undefined,
          });
        },
        isMultiple: true,
        showSelectAllOption: false,
      },
      {
        name: 'channel_source_id',
        placeholder: t('Emisora'),
        options: createTableFilterOptions({
          options: audienceChannelSourceOptions,
          allOptionLabel: t('Cualquier emisora'),
        }),
        withFilter: true,
        onFilterTextChange: setChannelSourceSearch,
        favoriteSelection: favoriteAudienceFilters?.channelIds,
        onSaveFavoriteSelection: newFavoriteSelection => {
          setFavoriteAudienceFilters({
            ...favoriteAudienceFilters,
            channelIds: newFavoriteSelection,
          });
        },
        onChange: value => {
          const values = Array.isArray(value) ? value : value ? [value] : [];
          setFilters({
            channelSourceIdIn: values.length > 0 ? values : undefined,
          });
        },
        isMultiple: true,
        showSelectAllOption: false,
      },
      {
        name: 'audience_state',
        placeholder: t('Estado audiencia'),
        options: [
          {
            label: t('Cualquier audiencia'),
            value: '',
          },
          {
            label: t('Audiencia asignada'),
            value: true,
          },
          {
            label: t('Audiencia no asignada'),
            value: false,
          },
        ],
        onChange: value => {
          setFilters({
            programSourceIdIsnull: typeof value === 'boolean' ? !value : null,
          });
        },
      },
    ],
    multiselect: multiSelectionButtonProps,
  };

  const columns: Column<TAudience>[] = [
    checkboxColumn,
    {
      id: 'name',
      title: t('Nombre'),
      render: item => {
        if (isLoadingProgramsAudienceProgramSources) {
          return <ItemSkeleton className="h-[20px] w-full" />;
        }
        const programsAudienceProgramSource = programsAudienceProgramSources.find(
          programsAudienceProgramSource =>
            programsAudienceProgramSource.id === item.program_source_id
        );

        return (
          <div className="flex items-center gap-2">
            <DragIcon className="cursor-grab" />

            <TableRow content={programsAudienceProgramSource?.name} />
          </div>
        );
      },
    },
    {
      id: 'datetime',
      title: t('Fecha y hora'),
      render: item => (
        <TableRow title={getDatetimeRangeFormatted(item.start_datetime, item.end_datetime)} />
      ),
    },
    {
      id: 'rating_percent',
      title: t('Rating %'),
      render: item => (
        <TableRow
          content={
            item.audience_rating_percent ? `${item.audience_rating_percent.toFixed(2)} %` : '0.00 %'
          }
        />
      ),
    },
    {
      id: 'audience',
      title: t('Audiencia'),
      render: item => <TableRow content={item.data.value} />,
    },
    {
      id: 'channel',
      title: t('Emisora'),
      render: item => {
        if (isLoadingAudienceChannelSources) {
          return <ItemSkeleton className="h-[20px] w-full" />;
        }

        return <TableRow content={audienceChannelSourceById.get(item.channel_source_id)?.name} />;
      },
    },
    {
      id: 'provider',
      title: t('Proveedor'),
      render: item => {
        if (isLoadingAudienceChannelSources) {
          return <ItemSkeleton className="h-[20px] w-full" />;
        }

        // A registry carries no provider of its own: it is the one of its source.
        const provider = audienceChannelSourceById.get(item.channel_source_id)?.provider;

        return <TableRow content={provider && AUDIENCE_PROVIDER_CONFIG[provider]?.label} />;
      },
    },
    {
      id: 'actions',
      title: '',
      className: 'items-end',
      width: '5%',
      render: item => (
        <RenderActionButtons
          item={item}
          onPreview={() => {
            routerManager.openNewTab({
              name: MediaManagerRouteEnum.SEGMENTS,
              searchParams: {
                gte: item.start_datetime,
                lte: item.end_datetime,
                audience_channel_source: item.channel_source_id,
              },
            });
          }}
          onAddScheduleEmisionIcon={() => {
            onAddScheduleEmision?.({
              all: false,
              selectedItems: [item],
              unselectedItems: [],
            });
          }}
          tooltips={{ preview: t('Ver franjas') }}
        />
      ),
    },
  ];

  return (
    <TablePaginated
      data={augmentedAudiences}
      options={options}
      columns={columns}
      isFetching={isLoadingAudiences}
      totalItems={total}
      ignoreEmpty
      ignoreWrap
      draggableRow
      showFilter
    />
  );
}
